import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { SearchSelect } from './SearchSelect.js';

const { t } = i18n;

interface LocationSlot {
  row: string;
  shelf: string;
  position: string;
  contents: { skuId: string; qty: number }[];
}

interface ReceivePalletBodyArgs {
  ownerFields: Record<string, unknown>;
  initialState: 'EXPECTED' | 'ARRIVED';
  whenDate: string;
  comment: string;
  count: number;
  slots: readonly LocationSlot[];
  includeContents: boolean;
}

export function buildReceivePalletBody(args: ReceivePalletBodyArgs): Record<string, unknown> {
  const body: Record<string, unknown> = {
    ...args.ownerFields,
    initialState: args.initialState,
    ...(args.initialState === 'ARRIVED'
      ? { arrivedAt: new Date(args.whenDate).toISOString() }
      : { expectedAt: args.whenDate }),
    comment: args.comment || undefined,
  };

  if (args.count === 1) {
    const slot = args.slots[0]!;
    body.count = 1;
    body.locationRow = slot.row.trim() || undefined;
    body.locationShelf = slot.shelf.trim() || undefined;
    body.locationPosition = slot.position.trim() || undefined;
    if (args.includeContents && slot.contents.length > 0) body.contents = slot.contents;
  } else {
    body.locations = args.slots.map((slot) => ({
      locationRow: slot.row.trim() || undefined,
      locationShelf: slot.shelf.trim() || undefined,
      locationPosition: slot.position.trim() || undefined,
      ...(args.includeContents && slot.contents.length > 0 ? { contents: slot.contents } : {}),
    }));
  }

  return body;
}

function emptySlot(): LocationSlot {
  return { row: '', shelf: '', position: '', contents: [] };
}

interface PartnerOpt {
  id: string;
  code: string;
  name: string;
}

interface SkuOpt {
  id: string;
  code: string;
  nameLt: string;
  nameEn?: string | null;
  type: 'PRODUCT' | 'COMPONENT';
}

/**
 * Receive one or more pallets at once. When `count` > 1 the form expands to N
 * location triplets — each physical pallet gets its own warehouse spot and
 * its own auto-issued pallet code. count=1 still works as before for
 * single-pallet receipts (2026-05-28 customer feedback).
 */
export function ReceivePalletDialog({
  customerId,
  onClose,
  onCreated,
}: {
  /**
   * Fixed storage-customer owner (customer-detail page). When omitted
   * (Warehouse page) the dialog shows a combined customer/partner owner
   * picker instead.
   */
  customerId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const standalone = !customerId;
  const today = new Date().toISOString().slice(0, 10);
  const [initialState, setInitialState] = useState<'EXPECTED' | 'ARRIVED'>('ARRIVED');
  const [whenDate, setWhenDate] = useState(today);
  const [comment, setComment] = useState('');
  const [count, setCount] = useState(1);
  const [slots, setSlots] = useState<LocationSlot[]>([emptySlot()]);
  const [partners, setPartners] = useState<PartnerOpt[]>([]);
  const [customers, setCustomers] = useState<PartnerOpt[]>([]);
  const [skus, setSkus] = useState<SkuOpt[]>([]);
  const [destinationPartnerId, setDestinationPartnerId] = useState('');
  // Standalone owner selection, encoded as `c:<id>` (customer) or `p:<id>`
  // (partner).
  const [owner, setOwner] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Active partners for the "destined for" dropdown (customer mode) and the
  // owner picker (standalone). Customers only needed for the standalone picker.
  useEffect(() => {
    api<PartnerOpt[]>('/partners/options')
      .then(setPartners)
      .catch(() => setPartners([]));
    if (standalone) {
      api<PartnerOpt[]>('/customers')
        .then(setCustomers)
        .catch(() => setCustomers([]));
    }
  }, [standalone]);

  const contentPartnerId = owner.startsWith('p:') ? owner.slice(2) : destinationPartnerId;

  useEffect(() => {
    if (!contentPartnerId) {
      setSkus([]);
      return;
    }
    api<SkuOpt[]>(`/partners/${contentPartnerId}/skus`)
      .then(setSkus)
      .catch(() => setSkus([]));
  }, [contentPartnerId]);

  // Keep the slot array length in sync with `count`. Extending appends empty
  // slots; shrinking drops trailing entries so any operator-entered data on
  // earlier rows is preserved.
  useEffect(() => {
    setSlots((prev) => {
      if (prev.length === count) return prev;
      if (count > prev.length) {
        return [...prev, ...Array.from({ length: count - prev.length }, emptySlot)];
      }
      return prev.slice(0, count);
    });
  }, [count]);

  function updateSlot(idx: number, patch: Partial<LocationSlot>) {
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  function addContentLine(idx: number) {
    if (!skus[0]) return;
    setSlots((prev) =>
      prev.map((slot, i) =>
        i === idx ? { ...slot, contents: [...slot.contents, { skuId: '', qty: 1 }] } : slot,
      ),
    );
  }

  function updateContentLine(
    slotIndex: number,
    lineIndex: number,
    patch: Partial<{ skuId: string; qty: number }>,
  ) {
    setSlots((prev) =>
      prev.map((slot, i) =>
        i === slotIndex
          ? {
              ...slot,
              contents: slot.contents.map((line, j) =>
                j === lineIndex ? { ...line, ...patch } : line,
              ),
            }
          : slot,
      ),
    );
  }

  const contentsValid = slots.every((slot) =>
    slot.contents.every((line) => Boolean(line.skuId) && line.qty > 0),
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!contentsValid) {
      setError(t('pallet.create.contents_required'));
      return;
    }

    // Resolve owner: fixed customer prop, or the standalone picker selection.
    let ownerFields: Record<string, unknown>;
    if (standalone) {
      if (!owner) {
        setError(t('pallet.create.owner_required'));
        return;
      }
      ownerFields = owner.startsWith('p:')
        ? { ownerPartnerId: owner.slice(2) }
        : { customerId: owner.slice(2), destinationPartnerId: destinationPartnerId || undefined };
    } else {
      ownerFields = { customerId, destinationPartnerId: destinationPartnerId || undefined };
    }

    setBusy(true);
    try {
      const body = buildReceivePalletBody({
        ownerFields,
        initialState,
        whenDate,
        comment,
        count,
        slots,
        includeContents: Boolean(contentPartnerId),
      });

      await api('/pallets', { method: 'POST', body });
      onCreated();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('pallet.create.title')} onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1"
      >
        {standalone ? (
          <label className="block space-y-1">
            <span className="font-medium">{t('pallet.create.owner')}</span>
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              <option value="">{t('pallet.create.owner_placeholder')}</option>
              <optgroup label={t('pallet.create.owner_customers')}>
                {customers.map((c) => (
                  <option key={`c:${c.id}`} value={`c:${c.id}`}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label={t('pallet.create.owner_partners')}>
                {partners.map((p) => (
                  <option key={`p:${p.id}`} value={`p:${p.id}`}>
                    {p.code} — {p.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
        ) : null}

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.create.initial_state')}</span>
          <select
            value={initialState}
            onChange={(e) => setInitialState(e.target.value as 'EXPECTED' | 'ARRIVED')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="ARRIVED">{t('pallet.state.ARRIVED')}</option>
            <option value="EXPECTED">{t('pallet.state.EXPECTED')}</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="font-medium">
            {initialState === 'ARRIVED' ? t('pallet.col.arrived') : t('pallet.col.expected')}
          </span>
          <input
            type="date"
            value={whenDate}
            onChange={(e) => setWhenDate(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.col.count')}</span>
          <NumberInput
            integer
            min={1}
            max={50}
            value={count}
            onChange={(v) => setCount(v ?? 1)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
          />
        </label>

        <div className="space-y-2">
          <span className="text-sm font-medium">{t('location.label')}</span>
          <ul className="space-y-2">
            {slots.map((slot, idx) => (
              <li key={idx} className="space-y-1">
                {count > 1 ? (
                  <span className="text-xs text-muted-foreground">
                    {t('pallet.create.pallet_n').replace('{{n}}', String(idx + 1))}
                  </span>
                ) : null}
                <div className="grid grid-cols-3 gap-2">
                  <input
                    value={slot.row}
                    onChange={(e) => updateSlot(idx, { row: e.target.value })}
                    maxLength={8}
                    placeholder={t('location.row')}
                    className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
                  />
                  <input
                    value={slot.shelf}
                    onChange={(e) => updateSlot(idx, { shelf: e.target.value })}
                    maxLength={8}
                    placeholder={t('location.shelf')}
                    className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
                  />
                  <input
                    value={slot.position}
                    onChange={(e) => updateSlot(idx, { position: e.target.value })}
                    maxLength={8}
                    placeholder={t('location.position')}
                    className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
                  />
                </div>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{t('location.hint')}</p>
        </div>

        {contentPartnerId ? (
          <div className="space-y-3 rounded border border-border p-3">
            <div>
              <div className="font-medium">{t('pallet.contents.title')}</div>
              <div className="text-xs text-muted-foreground">
                {t('pallet.create.contents_hint')}
              </div>
            </div>
            {slots.map((slot, slotIndex) => (
              <div key={slotIndex} className="space-y-2">
                {count > 1 ? (
                  <div className="text-xs font-medium">
                    {t('pallet.create.pallet_n').replace('{{n}}', String(slotIndex + 1))}
                  </div>
                ) : null}
                {slot.contents.map((line, lineIndex) => (
                  <div key={lineIndex} className="flex gap-2">
                    <SearchSelect
                      value={line.skuId}
                      onChange={(skuId) => updateContentLine(slotIndex, lineIndex, { skuId })}
                      ariaLabel={t('pallet.contents.pick_sku')}
                      placeholder={t('common.select')}
                      className="min-w-0 flex-1"
                      options={skus.map((sku) => ({
                        id: sku.id,
                        label: `${sku.code} — ${sku.nameLt} (${t(`sku.type.${sku.type}`)})`,
                        keywords: sku.nameEn ?? undefined,
                      }))}
                    />
                    <NumberInput
                      integer
                      min={1}
                      value={line.qty}
                      onChange={(qty) => updateContentLine(slotIndex, lineIndex, { qty: qty ?? 0 })}
                      className="w-24 rounded border border-border bg-background px-2 py-1.5 tabular-nums"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        updateSlot(slotIndex, {
                          contents: slot.contents.filter((_, i) => i !== lineIndex),
                        })
                      }
                      className="rounded border border-border px-2"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addContentLine(slotIndex)}
                  disabled={skus.length === 0 || slot.contents.some((line) => !line.skuId)}
                  className="rounded border border-border px-2 py-1 text-xs disabled:opacity-50"
                >
                  {t('pallet.unpack.add_line')}
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {!standalone ? (
          <label className="block space-y-1">
            <span className="font-medium">{t('pallet.create.destination_partner')}</span>
            <select
              value={destinationPartnerId}
              onChange={(e) => setDestinationPartnerId(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              <option value="">{t('pallet.create.destination_partner_none')}</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">
              {t('pallet.create.destination_partner_hint')}
            </span>
          </label>
        ) : null}

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.col.comment')}</span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
            placeholder={t('pallet.create.comment_hint')}
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !contentsValid}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('pallet.create.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
